import type { FlexProps, TextProps } from '@chakra-ui/react';
import { Flex, Skeleton, Text } from '@chakra-ui/react';
import { getFractionUnit } from '@ui/utils/getFractionUnit';
import type { FractionIsh, RoundingMode } from 'bi-fraction';
import { Fraction } from 'bi-fraction';
import { forwardRef, useMemo } from 'react';

import { FoldDecimal } from './FoldDecimal';

/**
 * Generic component for rendering number
 *
 * @component
 * @param {object} props The properties object.
 * @param {FractionIsh} props.value The FractionIsh value to display. If not provided, defaultDisplayValue will be used.
 * @param {string} props.defaultDisplayValue The value to display when the 'value' prop is not provided. Default is an empty string.
 * @param {number} props.decimalPlace The number of decimal places to format the value. Default is 2.
 * @param {string} props.groupSeparator The character used to separate groups of numbers, such as thousands from hundreds.
 * @param {React.ReactNode} props.prefix The element to display before the value.
 * @param {React.ReactNode} props.suffix The element to display after the value.
 * @param {React.Ref<HTMLDivElement>} props.ref The ref to be forwarded to the Flex component.
 * @param {boolean} props.isShowUnit The boolean to display unit.
 * @param {number} props.maxValue The max value to display.
 * @param {TextProps} props.textProps The props to be forwarded to the Text component, which is mainly for gradient text usage.
 * * @param {boolean} props.removeSignificantTrailingZeros Remove trailing zeros even if they're considered significant with given `decimalPlaces`. Default is false.
 * @returns {React.ElementType} Returns a Flex component with the formatted number and optional prefix and suffix.
 * Wrapped in a Skeleton component which shows a loading state until the 'value' prop is not `undefined`.
 */
export type NumProps = Omit<FlexProps, 'prefix'> & {
  value?: FractionIsh;
  defaultDisplayValue?: string;
  decimalPlaces?: number;
  groupSeparator?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  isShowUnit?: boolean;
  maxValue?: number;
  ref?: React.Ref<HTMLDivElement>;
  textProps?: TextProps;
  removeSignificantTrailingZeros?: boolean;
  roundingMode?: RoundingMode;
  foldDecimals?: boolean;
  showSignificantDecimalsForSmallValues?: boolean;
};

export const Num = forwardRef<HTMLDivElement, NumProps>(
  (
    {
      value,
      defaultDisplayValue,
      decimalPlaces = 2,
      groupSeparator = ',',
      prefix,
      suffix,
      isShowUnit,
      maxValue,
      textProps,
      removeSignificantTrailingZeros,
      roundingMode,
      foldDecimals: shouldFoldDecimals,
      showSignificantDecimalsForSmallValues,
      ...props
    },
    ref,
  ) => {
    const [displayValue, unit, needFold] = useMemo(() => {
      if (value === undefined) return [defaultDisplayValue, undefined];
      const fraction = new Fraction(value);
      const needFold =
        fraction.lt(`0.${'0'.repeat(decimalPlaces - 2)}1`) &&
        !showSignificantDecimalsForSmallValues;
      if (needFold && shouldFoldDecimals) {
        return foldDecimals({ fraction, decimalPlaces, roundingMode });
      }

      if (maxValue && fraction.gt(maxValue)) return [`>${maxValue}`, undefined];

      let nextFraction = fraction;
      let unit;
      if (isShowUnit) [nextFraction, unit] = getFractionUnit(fraction);

      let str: string;
      if (showSignificantDecimalsForSmallValues && fraction.lt(1)) {
        str = formatToSignificant(fraction, 6);
      } else {
        str = nextFraction.toFormat({
          decimalPlaces,
          trailingZeros: false,
          format: {
            groupSeparator,
          },
          roundingMode,
        });
      }

      if (removeSignificantTrailingZeros) {
        str = str.replace(/\.(\d*[^0])?0+$/, '.$1');
        str = str.replace(/\.$/, '');
      }

      return [str, unit, false];
    }, [
      value,
      defaultDisplayValue,
      maxValue,
      isShowUnit,
      decimalPlaces,
      groupSeparator,
      removeSignificantTrailingZeros,
      roundingMode,
      shouldFoldDecimals,
      showSignificantDecimalsForSmallValues,
    ]);

    const isLoading = !value && !displayValue;

    return (
      <Skeleton as={props.as} loading={isLoading}>
        <Flex align="center" gap="4px" {...props} ref={ref}>
          {/* need a placeholder here otherwise Skeleton won't show up when loading */}
          {isLoading && '--'}
          {prefix}
          <Text as="span" {...textProps}>
            {shouldFoldDecimals && needFold && displayValue ? (
              <FoldDecimal prependZeroCount={2} value={displayValue} />
            ) : (
              displayValue
            )}
            {unit}
          </Text>
          {suffix}
        </Flex>
      </Skeleton>
    );
  },
);

const foldDecimals = ({
  fraction,
  decimalPlaces,
  roundingMode,
}: {
  fraction: Fraction;
  decimalPlaces: Exclude<NumProps['decimalPlaces'], undefined>;
  roundingMode: NumProps['roundingMode'];
}) => {
  const firstNonZeroPosition = findFirstNonZeroPosition(fraction);
  // -3: remove 2 for the first "0.0"
  const digitsToShow = Math.max(1, firstNonZeroPosition + decimalPlaces - 3);
  const foldedValue = fraction.toFixed(digitsToShow, {
    roundingMode,
    trailingZeros: false,
  });
  return [foldedValue, undefined, true] as const;
};

const findFirstNonZeroPosition = (value: Fraction): number => {
  const strValue = value.toFixed(18);
  const decimalIndex = strValue.indexOf('.') + 1;
  const firstNonZeroIndex = strValue.slice(decimalIndex).search(/[1-9]/);
  if (firstNonZeroIndex === -1) {
    return -1;
  }
  return firstNonZeroIndex + 2;
};

const formatToSignificant = (
  fraction: Fraction,
  significantDigits: number,
): string => {
  const preciseStr = fraction.toFixed(18);
  const match = preciseStr.match(/^0\.(0*)(\d{0,18})/);
  if (match?.[2]) {
    const significantPart = match[2].slice(0, significantDigits);
    return `0.${match[1]}${significantPart}`;
  }
  return preciseStr;
};

Num.displayName = 'Num';
